import { Alert, Button, Card, Input, Space } from "antd";
import type { BizOperationHistoryVo, FenceReminderRecordsVo } from "@zhihaoscm/entity";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Dialog as ZhDialog,
    Grid as ZhGrid
} from "zhihao-ui-react";

import { DEFAULT_VALUE, LOG_DATE_FORMAT } from "@/config";
import { BIZ_OPERATION_HISTORY_DEF } from "@zhihaoscm/meta/src/bizOperationHistory";
import { ACTUAL_STATUS, MONITOR_BEHAVIOR, PROCESS_STATUS } from "@zhihaoscm/meta/src/fenceReminderRecords";
import { RECOGNIZE_RESULT, STATE } from "@zhihaoscm/meta/src/ship";
import { useUserStore } from "@/stores/react/authStore";
import { dateFormat } from "@/utils";
import { formatNumber } from "@/utils/number";

type FenceDetail = Partial<FenceReminderRecordsVo> & {
    fenceReminderRecords: NonNullable<Partial<FenceReminderRecordsVo>["fenceReminderRecords"]>;
};
export async function loadFenceRecord(id: string) {
    const { fenceReminderRecordService } = await import("@/api/services/fenceReminderRecordService");
    return fenceReminderRecordService.findVoById(id) as Promise<FenceDetail>;
}
export async function loadFenceProcessRecords(id: string) {
    const { bizOperationHistoryService } = await import("@/api/services/bizOperationHistoryService");
    return bizOperationHistoryService.getBizOperationHistorySelect({
        operationModule: BIZ_OPERATION_HISTORY_DEF.ELECTRONIC_FENCE_RECORDING_PROCESSING_HANDLE.code,
        bizNo: id
    });
}

type FenceSpecialist = {
    user?: {
        id?: number;
        personId?: number;
        name?: string;
        employeeId?: string;
    };
};

/** 复用 Vue 详情页的专员搜索接口，并兼容历史接口数组/包装对象响应。 */
export async function loadFenceSpecialists(keyword: string): Promise<FenceSpecialist[]> {
    const { fenceReminderRecordService } = await import("@/api/services/fenceReminderRecordService");
    const result = (await fenceReminderRecordService.findSpecials({ nameKeywords: keyword })) as unknown;
    if (Array.isArray(result)) return result as FenceSpecialist[];
    if (result && typeof result === "object" && Array.isArray((result as { data?: unknown }).data)) {
        return (result as { data: FenceSpecialist[] }).data;
    }
    return [];
}

/** 复用 Vue 详情页的单条指派接口，保持 ID 在路径、处理人 ID 在查询参数。 */
export async function assignFenceRecord(id: string, processorId: number) {
    const { fenceReminderRecordService } = await import("@/api/services/fenceReminderRecordService");
    return fenceReminderRecordService.assign(id, processorId);
}

export interface ElectronicFenceRecordDetailPageProps {
    loadRecord?: (id: string) => Promise<FenceDetail>;
    loadProcessRecords?: (id: string) => Promise<Partial<BizOperationHistoryVo>[]>;
    handleRecord?: (id: string, description: string) => Promise<unknown>;
    findSpecials?: (keyword: string) => Promise<FenceSpecialist[]>;
    assignRecord?: (id: string, processorId: number) => Promise<unknown>;
    currentUserId?: number;
    permissions?: string[];
}

/** 详情字段统一复用 zhihao-ui-react，同时保留 Vue 的缺省值显示口径。 */
const display = (value: unknown) =>
    value === undefined || value === null || value === "" ? DEFAULT_VALUE : String(value);

/** 将 Vue 接口返回的时间对象转换为详情页统一的时间文本。 */
const formatTriggerTime = (value: unknown) => {
    if (!value) return DEFAULT_VALUE;
    const source =
        typeof value === "object" && value !== null ? (value as { time?: unknown; value?: unknown }) : undefined;
    const time = source?.time ?? source?.value ?? value;
    return time ? dateFormat(String(time), LOG_DATE_FORMAT) : DEFAULT_VALUE;
};

/** 按 Vue 元数据展示状态，未知或空值统一回退为默认占位符。 */
const enumText = (meta: { getIntroByCode: (code?: number | string) => string | null }, value: unknown) =>
    value === undefined || value === null || value === ""
        ? DEFAULT_VALUE
        : meta.getIntroByCode(value as number | string) || DEFAULT_VALUE;

interface ProcessRecord {
    timestamp: string;
    handler: string;
    address: string;
    status: string;
    remark: string;
}

/** 从旧 Vue 操作内容中读取指定业务字段。 */
const getOperationValue = (item: Partial<BizOperationHistoryVo>, keys: string[]) => {
    const content = item.operationContent?.find(entry => keys.includes(String(entry.key)));
    return content?.value;
};

/** 保留 Vue 对数字状态码和非数字状态文本的兼容展示。 */
const formatActualStatus = (value: unknown) => {
    const isNumericValue =
        typeof value === "number" || (typeof value === "string" && value.trim() !== "" && !Number.isNaN(Number(value)));
    if (!isNumericValue) return display(value);
    return ACTUAL_STATUS.getIntroByCode(Number(value)) || String(value);
};

/** 将旧 Vue 操作历史转换为详情页处理记录展示模型。 */
const mapProcessRecord = (item: Partial<BizOperationHistoryVo>): ProcessRecord => {
    return {
        timestamp: formatTriggerTime(item.createdTime),
        handler: display(item.operatorName),
        address: display(getOperationValue(item, ["所在地", "location"])),
        status: formatActualStatus(getOperationValue(item, ["船舶实际状态", "actualStatus"])),
        remark: display(getOperationValue(item, ["处理说明"]))
    };
};

/** 电子围栏详情：处理按钮同时受状态、当前处理人和权限控制。 */
export function ElectronicFenceRecordDetailPage({
    loadRecord = loadFenceRecord,
    loadProcessRecords = loadFenceProcessRecords,
    handleRecord,
    findSpecials = loadFenceSpecialists,
    assignRecord = assignFenceRecord,
    currentUserId,
    permissions
}: ElectronicFenceRecordDetailPageProps) {
    const { id } = useParams();
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    const [data, setData] = useState<FenceDetail | null>(null);
    const [processRecords, setProcessRecords] = useState<ProcessRecord[]>([]);
    const [error, setError] = useState("");
    const [processing, setProcessing] = useState(false);
    const [description, setDescription] = useState("");
    const [assignOpen, setAssignOpen] = useState(false);
    const [assignSpecialists, setAssignSpecialists] = useState<FenceSpecialist[]>([]);
    const [selectedProcessorId, setSelectedProcessorId] = useState<number | null>(null);
    const [assignKeyword, setAssignKeyword] = useState("");
    const [assignLoading, setAssignLoading] = useState(false);
    const [assignError, setAssignError] = useState("");
    const [refreshToken, setRefreshToken] = useState(0);
    useEffect(() => {
        if (!id) return;
        void loadRecord(id)
            .then(setData)
            .catch(() => setError("电子围栏详情加载失败"));
        // 历史接口独立降级，避免处理记录异常阻断详情主体展示。
        void loadProcessRecords(id)
            .then(records => setProcessRecords(records.map(mapProcessRecord)))
            .catch(() => setProcessRecords([]));
    }, [id, loadProcessRecords, loadRecord, refreshToken]);
    if (error)
        return (
            <section aria-label="electronic-fence-record-detail-page">
                <Card>
                    <Alert message={error} type="error" />
                    <Button className="mt-4" onClick={() => navigate(-1)}>
                        返回
                    </Button>
                </Card>
            </section>
        );
    if (!data) return <Card loading />;
    const record = data.fenceReminderRecords;
    const currentPermissions = permissions ?? storePermissions;
    const canProcess =
        currentPermissions.includes("ROLE_ELECTRIC_FENCE_DEAL") &&
        Number(record.processStatus) === PROCESS_STATUS.FOLLOWING_UP.code &&
        Number(data.processorUser?.id) === Number(currentUserId);
    const canAssign =
        currentPermissions.includes("ROLE_ELECTRIC_FENCE_W") && [1, 2].includes(Number(record.processStatus));

    /** 打开 Vue 详情页同款指派弹窗，并先加载可选服务人员。 */
    const openAssign = async () => {
        setAssignOpen(true);
        setAssignError("");
        setAssignKeyword("");
        setSelectedProcessorId(data.processorUser?.id ?? null);
        setAssignLoading(true);
        try {
            setAssignSpecialists((await findSpecials("")) || []);
        } catch {
            setAssignSpecialists([]);
            setAssignError("专员查询失败");
        } finally {
            setAssignLoading(false);
        }
    };

    /** 按搜索词刷新指派人员，未选择人员时不触发指派请求。 */
    const searchAssignSpecialists = async () => {
        setAssignLoading(true);
        try {
            setAssignError("");
            setAssignSpecialists((await findSpecials(assignKeyword.trim())) || []);
        } catch {
            setAssignSpecialists([]);
            setAssignError("专员查询失败");
        } finally {
            setAssignLoading(false);
        }
    };

    /** 使用 Vue 的路径与查询参数契约提交指派，并刷新详情与处理记录。 */
    const submitAssign = async () => {
        if (!id || selectedProcessorId == null || assignLoading) {
            if (selectedProcessorId == null) setAssignError("请选择处理人");
            return;
        }
        setAssignLoading(true);
        try {
            await assignRecord(id, selectedProcessorId);
            setAssignOpen(false);
            setAssignError("");
            setRefreshToken(value => value + 1);
        } catch {
            setAssignError("指派失败");
        } finally {
            setAssignLoading(false);
        }
    };
    // 复用 Vue process.vue 的提交行为，成功关闭弹窗，失败保留上下文并提示错误。
    const submitProcess = async () => {
        if (!id || !handleRecord) {
            setProcessing(false);
            return;
        }
        try {
            await handleRecord(id, description);
            setProcessing(false);
        } catch {
            setError("电子围栏处理失败");
        }
    };
    return (
        <section aria-label="electronic-fence-record-detail-page">
            <Card>
                <ZhDetailHeader
                    title="处理详情"
                    leftInfos={[{ label: "编号", value: display(record.code || record.id) }]}
                    stateDesc={enumText(PROCESS_STATUS, record.processStatus)}
                />
                <ZhGrid cols={4} gap={[30, 20]}>
                    <ZhBaseItem label="提醒名称" value={display(record.reminderName)} />
                    <ZhBaseItem label="区域名称" value={display(record.markName)} />
                    <ZhBaseItem label="前一天位置" value={display(record.previousLocation)} />
                    <ZhBaseItem label="船舶行为" value={enumText(MONITOR_BEHAVIOR, record.shipBehavior)} />
                    <ZhBaseItem label="MMSI">
                        {data.ship?.id ? (
                            <Link to={`/logistics/shipManage/shipInfo/shipInfoDetail/${data.ship.id}`}>
                                {display(record.mmsi || data.ship.id)}
                            </Link>
                        ) : (
                            display(record.mmsi)
                        )}
                    </ZhBaseItem>
                    <ZhBaseItem label="船名" value={display(record.shipName)} />
                    <ZhBaseItem label="载重吨" value={formatNumber(data.ship?.tonCapacity)} />
                    <ZhBaseItem label="认证状态" value={enumText(STATE, data.ship?.state)} />
                    <ZhBaseItem label="货仓状态" value={enumText(RECOGNIZE_RESULT, data.ship?.warehouseState)} />
                    <ZhBaseItem label="运单编号">
                        {data.transportOrderShipId ? (
                            <Link to={`/logistics/shippingNeed/waybillManagementInfo/${data.transportOrderShipId}`}>
                                {data.transportOrderShipId}
                            </Link>
                        ) : (
                            display(data.transportOrderShipId)
                        )}
                    </ZhBaseItem>
                    <ZhBaseItem label="触发时间" value={formatTriggerTime(record.triggerTime)} />
                    <ZhBaseItem label="所在地" value={display(record.location)} />
                    <ZhBaseItem label="船舶实际状态" value={enumText(ACTUAL_STATUS, record.actualStatus)} />
                    <ZhBaseItem label="处理人" value={display(data.processorUser?.name)} />
                    <ZhBaseItem label="当前项目" value={display(data.projectName)} />
                </ZhGrid>
                <section aria-label="电子围栏处理记录" className="mt-6">
                    <div className="mb-4 text-base font-medium">处理记录</div>
                    {processRecords.length ? (
                        processRecords.map((processRecord, index) => (
                            <div key={`${processRecord.timestamp}-${index}`} className="mb-4 last:mb-0">
                                <div className="mb-2 text-sm">
                                    {processRecord.timestamp} 处理人：{processRecord.handler}
                                </div>
                                <ZhGrid cols={8} gap={[0, 16]}>
                                    <ZhBaseItem label="船舶实际状态" value={processRecord.status} span={1} />
                                    <ZhBaseItem label="所在地" value={processRecord.address} span={7} />
                                    <ZhBaseItem label="处理说明" value={processRecord.remark} span={8} />
                                </ZhGrid>
                            </div>
                        ))
                    ) : (
                        <div>暂无处理记录</div>
                    )}
                </section>
                <Space className="mt-4">
                    {canAssign ? (
                        <Button disabled={banButton} type="primary" onClick={() => void openAssign()}>
                            {data.processorUser?.id != null ? "变更指派" : "指派"}
                        </Button>
                    ) : null}
                    {canProcess ? (
                        <Button disabled={banButton} type="primary" onClick={() => setProcessing(true)}>
                            处理
                        </Button>
                    ) : null}
                    {record.mmsi || data.ship?.id ? (
                        <Button
                            type="link"
                            onClick={() =>
                                navigate(
                                    `/logistics/findTrack/${encodeURIComponent(record.mmsi || data.ship?.id || "")}/latest`
                                )
                            }
                        >
                            查询轨迹
                        </Button>
                    ) : null}
                    <Button onClick={() => navigate(-1)}>返回</Button>
                </Space>
                <ZhDialog
                    open={assignOpen}
                    title="指派处理人"
                    width={400}
                    onCancel={() => {
                        if (!assignLoading) setAssignOpen(false);
                    }}
                    footer={
                        <Space>
                            <Button disabled={assignLoading} onClick={() => setAssignOpen(false)}>
                                取消
                            </Button>
                            <Button
                                aria-label="确认指派"
                                disabled={selectedProcessorId == null}
                                loading={assignLoading}
                                type="primary"
                                onClick={() => void submitAssign()}
                            >
                                确认指派
                            </Button>
                        </Space>
                    }
                >
                    <Space.Compact block>
                        <Input
                            aria-label="专员姓名"
                            placeholder="请输入姓名搜索"
                            value={assignKeyword}
                            onChange={event => setAssignKeyword(event.target.value)}
                        />
                        <Button type="primary" onClick={() => void searchAssignSpecialists()}>
                            查询专员
                        </Button>
                    </Space.Compact>
                    {assignError ? <p role="alert">{assignError}</p> : null}
                    <div aria-label="专员列表" className="mt-3 max-h-72 overflow-auto" role="radiogroup">
                        {assignSpecialists.length ? (
                            assignSpecialists.map(specialist => {
                                const personId = specialist.user?.personId ?? specialist.user?.id;
                                if (personId == null) return null;
                                return (
                                    <label key={personId} className="mb-2 block">
                                        <input
                                            aria-label={specialist.user?.name || String(personId)}
                                            checked={selectedProcessorId === personId}
                                            name="electronic-fence-handler"
                                            type="radio"
                                            onChange={() => setSelectedProcessorId(personId)}
                                        />
                                        <span className="ml-2">
                                            {specialist.user?.name || "未命名专员"} (
                                            {specialist.user?.employeeId || "-"})
                                        </span>
                                    </label>
                                );
                            })
                        ) : (
                            <div>暂未设定可指派的服务人员</div>
                        )}
                    </div>
                </ZhDialog>
                <ZhDialog
                    open={processing}
                    title="处理"
                    width={400}
                    onCancel={() => setProcessing(false)}
                    footer={
                        <Space>
                            <Button aria-label="取消" onClick={() => setProcessing(false)}>
                                取消
                            </Button>
                            <Button aria-label="确认处理" type="primary" onClick={() => void submitProcess()}>
                                确认处理
                            </Button>
                        </Space>
                    }
                >
                    <Input.TextArea
                        aria-label="处理说明"
                        value={description}
                        onChange={event => setDescription(event.target.value)}
                    />
                </ZhDialog>
            </Card>
        </section>
    );
}

export default ElectronicFenceRecordDetailPage;
